import React, { useEffect, useMemo, useState } from "react";
import { Search, ArrowUpRight, ArrowDownRight, Info } from "lucide-react";
import { api } from "@/lib/api";
import { fmtPct, fmtUSD, fmtUSDExact } from "@/lib/format";
import TradeDialog from "@/components/TradeDialog";

const TABS = [
  { id: "crypto", label: "Crypto", path: "/markets/crypto", group: "crypto" },
  { id: "stock", label: "Stocks", path: "/markets/stocks", group: "stocks" },
  { id: "bond", label: "Bonds", path: "/markets/bonds", group: "bonds" },
];

export default function Markets() {
  const [tab, setTab] = useState("crypto");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [sources, setSources] = useState({});

  useEffect(() => {
    api
      .get("/markets/sources")
      .then(({ data }) => setSources(data))
      .catch(() => setSources({}));
  }, [rows]);

  const activeGroup = useMemo(() => TABS.find((t) => t.id === tab)?.group, [tab]);
  const activeSimulated = sources?.[activeGroup]?.simulated === true;

  useEffect(() => {
    const cur = TABS.find((t) => t.id === tab);
    setLoading(true);
    api
      .get(cur.path)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [tab]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) => r.symbol.toLowerCase().includes(s) || r.name.toLowerCase().includes(s));
  }, [q, rows]);

  const openTrade = (r) => {
    setSelected(r);
    setOpen(true);
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="markets-page">
      <header className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            // markets · {tab}
          </div>
          <h1 className="mt-2 text-4xl md:text-5xl font-black tracking-tighter uppercase">
            Markets
          </h1>
        </div>
        <div className="relative w-full md:w-80" data-testid="markets-search-wrap">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <input
            data-testid="markets-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search symbol or name…"
            className="w-full bg-transparent border border-neo-border pl-9 pr-3 py-2.5 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-white transition-colors"
          />
        </div>
      </header>

      <div className="flex border border-neo-border" data-testid="markets-tabs">
        {TABS.map((t) => {
          const simulated = sources?.[t.group]?.simulated === true;
          return (
            <button
              key={t.id}
              data-testid={`markets-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex-1 py-3 font-mono uppercase tracking-widest text-xs border-r border-neo-border last:border-r-0 transition-colors flex items-center justify-center gap-2 ${
                tab === t.id ? "bg-white text-black" : "text-neutral-400 hover:bg-white/5"
              }`}
            >
              {t.label}
              {simulated && (
                <span
                  data-testid={`markets-sim-badge-${t.id}`}
                  title="Simulated prices — add a live data key to go live"
                  className={`px-1.5 py-0.5 text-[8px] leading-none tracking-[0.2em] border ${
                    tab === t.id
                      ? "border-black/40 text-black/70"
                      : "border-amber-500/40 text-amber-400/80"
                  }`}
                >
                  SIM
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activeSimulated && (
        <div
          data-testid="markets-sim-banner"
          className="mt-4 flex items-start gap-3 border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-amber-200/80 font-mono leading-relaxed"
        >
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-400/80" strokeWidth={1.5} />
          <span>
            Simulated prices — {sources?.[activeGroup]?.label || "fallback"} feed. Add a live
            Alpha Vantage key in Admin → Providers to switch this market to real-time data. This
            badge disappears automatically once live data is active.
          </span>
        </div>
      )}

      <div className="mt-6 border border-neo-border bg-neo-surface" data-testid="markets-table">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-neo-border font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          <div className="col-span-1">#</div>
          <div className="col-span-2">Sym</div>
          <div className="col-span-4">Name</div>
          <div className="col-span-2 text-right">Price</div>
          <div className="col-span-2 text-right">24h</div>
          <div className="col-span-1 text-right">Trade</div>
        </div>
        <div className="max-h-[70vh] overflow-y-auto divide-y divide-neo-border font-mono text-sm">
          {loading && (
            <div className="p-8 text-center text-neutral-500 font-mono text-xs uppercase tracking-widest">
              Loading market data…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="p-8 text-center text-neutral-500 font-mono text-xs">No matches</div>
          )}
          {filtered.map((r, i) => (
            <div
              key={`${r.type}-${r.id || r.symbol}`}
              className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-white/5 transition-colors items-center"
              data-testid={`market-row-${r.symbol}`}
            >
              <div className="col-span-1 text-neutral-500 text-xs">{i + 1}</div>
              <div className="col-span-2 flex items-center gap-2">
                {r.image ? (
                  <img alt="" src={r.image} className="w-5 h-5" />
                ) : (
                  <div className="w-5 h-5 border border-neo-border flex items-center justify-center text-[9px]">
                    {r.symbol.slice(0, 2)}
                  </div>
                )}
                <span className="text-white uppercase">{r.symbol}</span>
              </div>
              <div className="col-span-4 truncate text-neutral-300">{r.name}</div>
              <div className="col-span-2 text-right text-white">{fmtUSDExact(r.price, r.price < 1 ? 4 : 2)}</div>
              <div className={`col-span-2 text-right flex items-center justify-end gap-1 ${r.change_24h >= 0 ? "pos" : "neg"}`}>
                {r.change_24h >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {fmtPct(r.change_24h)}
              </div>
              <div className="col-span-1 text-right">
                <button
                  data-testid={`market-trade-btn-${r.symbol}`}
                  onClick={() => openTrade(r)}
                  className="border border-white px-2 py-1 text-[10px] uppercase tracking-widest hover-invert"
                >
                  Trade
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <TradeDialog open={open} onOpenChange={setOpen} asset={selected} />
    </div>
  );
}
